import React, { useEffect, useState } from "react";
import ColorInput from './ColorInput';

const clampNumber = (raw, min, max) => {
    if (raw === "" || raw == null) return undefined;
    const n = Number(raw);
    if (!Number.isFinite(n)) return undefined;
    return Math.min(max, Math.max(min, n));
};

const NumberWithSlider = ({
    label,
    value,
    min,
    max,
    step = 1,
    onChange,
    labelStyle,
    labelHintStyle,
    controlStyle,
    rangeStyle,
}) => {
    const sliderValue = Number.isFinite(value) ? value : min;
    const numberValue = Number.isFinite(value) ? value : "";
    return (
        <label style={labelStyle}>
            <span style={labelHintStyle}>{label}</span>
            <div style={{ display: "grid", gap: 6, minWidth: 0 }}>
                <input
                    type="range"
                    min={min}
                    max={max}
                    step={step}
                    value={sliderValue}
                    onChange={(e) => onChange(e.target.value)}
                    onPointerDown={(e) => {
                        if (e.pointerId != null && e.currentTarget.setPointerCapture) {
                            e.currentTarget.setPointerCapture(e.pointerId);
                        }
                    }}
                    onPointerUp={(e) => {
                        if (e.pointerId != null && e.currentTarget.releasePointerCapture) {
                            e.currentTarget.releasePointerCapture(e.pointerId);
                        }
                    }}
                    onPointerCancel={(e) => {
                        if (e.pointerId != null && e.currentTarget.releasePointerCapture) {
                            e.currentTarget.releasePointerCapture(e.pointerId);
                        }
                    }}
                    style={rangeStyle}
                />
                <input
                    type="number"
                    min={min}
                    max={max}
                    step={step}
                    value={numberValue}
                    onChange={(e) => onChange(e.target.value)}
                    style={controlStyle}
                />
            </div>
        </label>
    );
};

const Section = ({
    title,
    isOpen,
    onToggle,
    children,
    borderColor,
    textColor,
    buttonStyle,
}) => (
    <div style={{ margin: "12px 0", borderTop: `1px solid ${borderColor}`, paddingTop: 8 }}>
        <div
            style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "4px 0",
                userSelect: "none",
                color: textColor,
                fontWeight: 600,
                fontSize: 14,
            }}
        >
            <span>{title}</span>
            <button
                type="button"
                onClick={onToggle}
                aria-expanded={isOpen}
                style={{
                    ...buttonStyle,
                    padding: "2px 8px",
                    minWidth: 28,
                }}
                title={isOpen ? "Collapse section" : "Expand section"}
            >
                {isOpen ? "v" : ">"}
            </button>
        </div>
        {isOpen && <div style={{ marginTop: 6 }}>{children}</div>}
    </div>
);

export default function PostStyleControls({
    style = {},
    onStyleChange,
    onBackgroundImageChange,
    onCoverChange,
}) {
    const [open, setOpen] = useState(() => {
        try {
            return localStorage.getItem("postStyleControlsOpen") === "true";
        } catch {
            return false;
        }
    });
    const [theme, setTheme] = useState(() => {
        try {
            const saved = localStorage.getItem("postStyleTheme");
            return saved === "dark" ? "dark" : "light";
        } catch {
            return "light";
        }
    });
    useEffect(() => {
        try {
            localStorage.setItem("postStyleTheme", theme);
        } catch {
        }
    }, [theme]);
    useEffect(() => {
        try {
            localStorage.setItem("postStyleControlsOpen", open ? "true" : "false");
        } catch {
        }
    }, [open]);

    const updateStyle = (changes) => {
        onStyleChange?.({ ...style, ...changes });
    };

    const bgType = style.backgroundType || "color";
    const initGrad = style.gradient || {
        colors: ["#ffffff", "#000000"],
        direction: "to right",
    };
    const borderWidthValue = Number.isFinite(style.borderWidth)
        ? style.borderWidth
        : Number.isFinite(parseInt(style.borderWidth, 10))
            ? parseInt(style.borderWidth, 10)
            : undefined;

    const handleBgTypeChange = (e) =>
        updateStyle({
            backgroundType: e.target.value,
            backgroundImage: "",
            backgroundColor: "",
        });
    const handleColorChange = (e) =>
        updateStyle({ backgroundColor: e.target.value, backgroundImage: "" });

    const handleGradientColor = (pos, value) => {
        const grad = { ...initGrad, colors: [...initGrad.colors] };
        grad.colors[pos] = value;
        updateStyle({
            backgroundImage: `linear-gradient(${grad.direction}, ${grad.colors[0]}, ${grad.colors[1]})`,
            gradient: grad,
            backgroundColor: "",
        });
    };
    const handleGradientDirection = (e) => {
        const grad = { ...initGrad, direction: e.target.value };
        updateStyle({
            backgroundImage: `linear-gradient(${grad.direction}, ${grad.colors[0]}, ${grad.colors[1]})`,
            gradient: grad,
            backgroundColor: "",
        });
    };

    const handleBorderColorChange = (e) => updateStyle({ borderColor: e.target.value });
    const handleBorderStyleChange = (e) => updateStyle({ borderStyle: e.target.value });
    const handleBorderWidthChange = (raw) => {
        const value = raw?.target ? raw.target.value : raw;
        updateStyle({ borderWidth: clampNumber(value, 0, 20) });
    };

    const palettes = {
        light: {
            surface: "#ffffff",
            surfaceSoft: "#f7f7fb",
            text: "#0f172a",
            textMuted: "#475569",
            border: "#CBD5E1",
            controlBg: "#ffffff",
            controlText: "#0f172a",
            controlBorder: "#94A3B8",
            buttonBg: "#ffffff",
            buttonBgHover: "#E2E8F0",
            focus: "#60A5FA",
        },
        dark: {
            surface: "#0b1220",
            surfaceSoft: "#0f172a",
            text: "#E2E8F0",
            textMuted: "#94A3B8",
            border: "#273244",
            controlBg: "#111827",
            controlText: "#E5E7EB",
            controlBorder: "#334155",
            buttonBg: "#111827",
            buttonBgHover: "#1f2937",
            focus: "#38BDF8",
        },
    };
    const cc = palettes[theme];

    const ccStyles = {
        container: {
            background: cc.surface,
            color: cc.text,
            border: `1px solid ${cc.border}`,
            borderRadius: 12,
            padding: 12,
        },
        label: {
            display: "grid",
            gap: 6,
            background: cc.surfaceSoft,
            color: cc.text,
            border: `1px solid ${cc.border}`,
            borderRadius: 10,
            padding: 8,
            margin: 3,
            boxSizing: "border-box",
            maxWidth: "100%",
            minWidth: 0,
        },
        heading: {
            background: "transparent",
            border: "none",
            color: cc.text,
            fontSize: 14,
            fontWeight: 600,
            margin: "8px 0 4px 0",
            padding: 0,
        },
        labelHint: { fontSize: 12, color: cc.textMuted },
        control: {
            width: "100%",
            background: cc.controlBg,
            color: cc.controlText,
            border: `1px solid ${cc.controlBorder}`,
            borderRadius: 10,
            padding: "6px 10px",
            outline: "none",
            boxSizing: "border-box",
            maxWidth: "100%",
            minWidth: 0,
        },
        range: {
            width: "100%",
            background: cc.controlBg,
            color: cc.controlText,
            border: `1px solid ${cc.controlBorder}`,
            borderRadius: 10,
            padding: 0,
            height: 28,
            outline: "none",
            boxSizing: "border-box",
            maxWidth: "100%",
            minWidth: 0,
        },
        button: {
            background: cc.buttonBg,
            color: cc.controlText,
            border: `1px solid ${cc.controlBorder}`,
            borderRadius: 10,
            padding: "6px 10px",
            cursor: "pointer",
        },
        title: {
            background: "transparent",
            border: "none",
            color: cc.text,
            fontSize: 18,
            fontWeight: 700,
            margin: "4px 0 10px 0",
            padding: 0,
            textAlign: "left",
        },
    };

    const [sections, setSections] = useState(() => {
        try {
            const saved = localStorage.getItem("postStyleControlsSections");
            if (saved) {
                const parsed = JSON.parse(saved);
                return {
                    background: Boolean(parsed?.background),
                    font: Boolean(parsed?.font),
                    borders: Boolean(parsed?.borders),
                };
            }
        } catch {
        }
        return {
            background: false,
            font: false,
            borders: false,
        };
    });
    useEffect(() => {
        try {
            localStorage.setItem("postStyleControlsSections", JSON.stringify(sections));
        } catch {
        }
    }, [sections]);

    const toggleSection = (key) =>
        setSections((prev) => ({ ...prev, [key]: !prev[key] }));

    return (
        <div className="post-style-controls" style={ccStyles.container}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <span style={ccStyles.labelHint}>Post Style Controls</span>
                <button
                    type="button"
                    onClick={() => setOpen((v) => !v)}
                    style={ccStyles.button}
                >
                    {open ? "Hide" : "Edit"}
                </button>
            </div>

            {open && (
                <div style={{ marginTop: 8 }}>
                    <h1 style={ccStyles.title}>Customize Post Style</h1>

                    <button
                        type="button"
                        onClick={() => setTheme((t) => (t === "light" ? "dark" : "light"))}
                        style={ccStyles.button}
                    >
                        {theme === "light" ? "Dark" : "Light"}
                    </button>

                    <Section
                        title="Background Controls"
                        isOpen={Boolean(sections.background)}
                        onToggle={() => toggleSection("background")}
                        borderColor={cc.border}
                        textColor={cc.text}
                        buttonStyle={ccStyles.button}
                    >
                        <div style={{ display: "grid", gap: 8 }}>
                            <label style={ccStyles.label}>
                                <span style={ccStyles.labelHint}>Background Type</span>
                                <select value={bgType} onChange={handleBgTypeChange} style={ccStyles.control}>
                                    <option value="color">Color</option>
                                    <option value="image">Image</option>
                                    <option value="cover">Cover</option>
                                    <option value="gradient">Gradient</option>
                                </select>
                            </label>

                            {bgType === "color" && (
                                <label style={ccStyles.label}>
                                    <span style={ccStyles.labelHint}>Color</span>
                                    <ColorInput
                                        value={style.backgroundColor || "#ffffff"}
                                        onChange={handleColorChange}
                                        style={ccStyles.control}
                                    />
                                </label>
                            )}

                            {bgType === "image" && (
                                <label style={ccStyles.label}>
                                    <span style={ccStyles.labelHint}>Upload Image</span>
                                    <input
                                        type="file"
                                        accept="image/*"
                                        style={ccStyles.control}
                                        onChange={(e) => onBackgroundImageChange?.(e.target.files?.[0])}
                                    />
                                </label>
                            )}

                            {bgType === "cover" && (
                                <label style={ccStyles.label}>
                                    <span style={ccStyles.labelHint}>Upload Cover</span>
                                    <input
                                        type="file"
                                        accept="image/*"
                                        style={ccStyles.control}
                                        onChange={(e) => onCoverChange?.(e.target.files?.[0])}
                                    />
                                </label>
                            )}

                            {bgType === "gradient" && (
                                <>
                                    <label style={ccStyles.label}>
                                        <span style={ccStyles.labelHint}>From</span>
                                        <ColorInput
                                            value={style.gradient?.colors?.[0] || "#ffffff"}
                                            onChange={(e) => handleGradientColor(0, e.target.value)}
                                            style={ccStyles.control}
                                        />
                                    </label>
                                    <label style={ccStyles.label}>
                                        <span style={ccStyles.labelHint}>To</span>
                                        <ColorInput
                                            value={style.gradient?.colors?.[1] || "#000000"}
                                            onChange={(e) => handleGradientColor(1, e.target.value)}
                                            style={ccStyles.control}
                                        />
                                    </label>
                                    <label style={ccStyles.label}>
                                        <span style={ccStyles.labelHint}>Direction</span>
                                        <select
                                            value={style.gradient?.direction || "to right"}
                                            onChange={handleGradientDirection}
                                            style={ccStyles.control}
                                        >
                                            <option value="to right">Right</option>
                                            <option value="to bottom">Down</option>
                                            <option value="45deg">Diagonal</option>
                                        </select>
                                    </label>
                                </>
                            )}
                        </div>
                    </Section>

                    <Section
                        title="Font Controls"
                        isOpen={Boolean(sections.font)}
                        onToggle={() => toggleSection("font")}
                        borderColor={cc.border}
                        textColor={cc.text}
                        buttonStyle={ccStyles.button}
                    >
                        <div style={{ display: "grid", gap: 8 }}>
                            <label style={ccStyles.label}>
                                <span style={ccStyles.labelHint}>Text Align</span>
                                <select
                                    value={style.textAlign || "left"}
                                    onChange={(e) => updateStyle({ textAlign: e.target.value })}
                                    style={ccStyles.control}
                                >
                                    <option value="left">Left</option>
                                    <option value="center">Center</option>
                                    <option value="right">Right</option>
                                    <option value="justify">Justify</option>
                                </select>
                            </label>

                            <label style={ccStyles.label}>
                                <span style={ccStyles.labelHint}>General Font Color</span>
                                <ColorInput
                                    value={style.color || "#000000"}
                                    onChange={(e) => updateStyle({ color: e.target.value })}
                                    style={ccStyles.control}
                                />
                            </label>

                            <label style={ccStyles.label}>
                                <span style={ccStyles.labelHint}>Title Color</span>
                                <ColorInput
                                    value={style.headers?.color || ""}
                                    onChange={(e) =>
                                        updateStyle({
                                            headers: { ...(style.headers || {}), color: e.target.value || undefined },
                                        })
                                    }
                                    style={ccStyles.control}
                                />
                            </label>

                            <label style={ccStyles.label}>
                                <span style={ccStyles.labelHint}>Paragraph Color</span>
                                <ColorInput
                                    value={style.paragraph?.color || ""}
                                    onChange={(e) =>
                                        updateStyle({
                                            paragraph: {
                                                ...(style.paragraph || {}),
                                                color: e.target.value || undefined,
                                            },
                                        })
                                    }
                                    style={ccStyles.control}
                                />
                            </label>

                            <label style={ccStyles.label}>
                                <span style={ccStyles.labelHint}>Header Font Family</span>
                                <select
                                    value={style.headers?.fontFamily || ""}
                                    onChange={(e) =>
                                        updateStyle({
                                            headers: { ...(style.headers || {}), fontFamily: e.target.value },
                                        })
                                    }
                                    style={ccStyles.control}
                                >
                                    <option value="">-- use default --</option>
                                    <option value="system-ui">system-ui</option>
                                    <option value="Inter, sans-serif">Inter, sans-serif</option>
                                    <option value="Roboto, sans-serif">Roboto, sans-serif</option>
                                    <option value="Montserrat, sans-serif">Montserrat, sans-serif</option>
                                    <option value="Georgia, serif">Georgia, serif</option>
                                    <option value="'Times New Roman', serif">Times New Roman, serif</option>
                                    <option value="'Courier New', monospace">Courier New, monospace</option>
                                </select>
                            </label>

                            <NumberWithSlider
                                label="Header Size (px)"
                                min={10}
                                max={96}
                                value={style.headers?.fontSize}
                                onChange={(next) =>
                                    updateStyle({
                                        headers: {
                                            ...(style.headers || {}),
                                            fontSize: clampNumber(next, 10, 96),
                                        },
                                    })
                                }
                                labelStyle={ccStyles.label}
                                labelHintStyle={ccStyles.labelHint}
                                controlStyle={ccStyles.control}
                                rangeStyle={ccStyles.range}
                            />

                            <label style={ccStyles.label}>
                                <span style={ccStyles.labelHint}>Header Weight</span>
                                <select
                                    value={style.headers?.fontWeight ?? ""}
                                    onChange={(e) =>
                                        updateStyle({
                                            headers: {
                                                ...(style.headers || {}),
                                                fontWeight: e.target.value ? Number(e.target.value) : undefined,
                                            },
                                        })
                                    }
                                    style={ccStyles.control}
                                >
                                    <option value="">-- default --</option>
                                    {[400, 500, 600, 700, 800].map((w) => (
                                        <option key={w} value={w}>
                                            {w}
                                        </option>
                                    ))}
                                </select>
                            </label>

                            <label style={ccStyles.label}>
                                <span style={ccStyles.labelHint}>Paragraph Family</span>
                                <select
                                    value={style.paragraph?.fontFamily || ""}
                                    onChange={(e) =>
                                        updateStyle({
                                            paragraph: { ...(style.paragraph || {}), fontFamily: e.target.value },
                                        })
                                    }
                                    style={ccStyles.control}
                                >
                                    <option value="">-- use default --</option>
                                    <option value="system-ui">system-ui</option>
                                    <option value="Inter, sans-serif">Inter, sans-serif</option>
                                    <option value="Roboto, sans-serif">Roboto, sans-serif</option>
                                    <option value="Montserrat, sans-serif">Montserrat, sans-serif</option>
                                    <option value="Georgia, serif">Georgia, serif</option>
                                    <option value="'Times New Roman', serif">Times New Roman, serif</option>
                                    <option value="'Courier New', monospace">Courier New, monospace</option>
                                </select>
                            </label>

                            <NumberWithSlider
                                label="Paragraph Size (px)"
                                min={10}
                                max={48}
                                value={style.paragraph?.fontSize}
                                onChange={(next) =>
                                    updateStyle({
                                        paragraph: {
                                            ...(style.paragraph || {}),
                                            fontSize: clampNumber(next, 10, 48),
                                        },
                                    })
                                }
                                labelStyle={ccStyles.label}
                                labelHintStyle={ccStyles.labelHint}
                                controlStyle={ccStyles.control}
                                rangeStyle={ccStyles.range}
                            />

                            <label style={ccStyles.label}>
                                <span style={ccStyles.labelHint}>Link Color</span>
                                <ColorInput
                                    value={style.links?.color || ""}
                                    onChange={(e) =>
                                        updateStyle({
                                            links: { ...(style.links || {}), color: e.target.value },
                                        })
                                    }
                                    style={ccStyles.control}
                                />
                            </label>

                            <label style={ccStyles.label}>
                                <span style={ccStyles.labelHint}>Link Decoration</span>
                                <select
                                    value={style.links?.textDecoration || ""}
                                    onChange={(e) =>
                                        updateStyle({
                                            links: { ...(style.links || {}), textDecoration: e.target.value },
                                        })
                                    }
                                    style={ccStyles.control}
                                >
                                    <option value="">-- default --</option>
                                    {["none", "underline", "overline", "line-through"].map((d) => (
                                        <option key={d} value={d}>
                                            {d}
                                        </option>
                                    ))}
                                </select>
                            </label>
                        </div>
                    </Section>

                    <Section
                        title="Border Controls"
                        isOpen={Boolean(sections.borders)}
                        onToggle={() => toggleSection("borders")}
                        borderColor={cc.border}
                        textColor={cc.text}
                        buttonStyle={ccStyles.button}
                    >
                        <div style={{ display: "grid", gap: 8 }}>
                            <label style={ccStyles.label}>
                                <span style={ccStyles.labelHint}>Border Style</span>
                                <select
                                    value={style.borderStyle || "solid"}
                                    onChange={handleBorderStyleChange}
                                    style={ccStyles.control}
                                >
                                    <option value="solid">Solid</option>
                                    <option value="dashed">Dashed</option>
                                    <option value="dotted">Dotted</option>
                                    <option value="double">Double</option>
                                    <option value="none">None</option>
                                </select>
                            </label>

                            <NumberWithSlider
                                label="Border Width (px)"
                                min={0}
                                max={20}
                                value={borderWidthValue}
                                onChange={handleBorderWidthChange}
                                labelStyle={ccStyles.label}
                                labelHintStyle={ccStyles.labelHint}
                                controlStyle={ccStyles.control}
                                rangeStyle={ccStyles.range}
                            />

                            <label style={ccStyles.label}>
                                <span style={ccStyles.labelHint}>Border Color</span>
                                <ColorInput
                                    value={style.borderColor || "#000000"}
                                    onChange={handleBorderColorChange}
                                    style={ccStyles.control}
                                />
                            </label>
                        </div>
                    </Section>

                    <button
                        type="button"
                        onClick={() => onStyleChange?.({})}
                        style={ccStyles.button}
                    >
                        Reset Post Style
                    </button>
                </div>
            )}
        </div>
    );
}
